/**
 * Display helpers on top of shared/hebrewCalendar.ts. Business dates are 'YYYY-MM-DD' local keys
 * (toDateKey) — never toISOString().
 */
import {
  toDateKey, fromDateKey, hebrewDateString, isShabbat, parashaLabel, holidaysOn, shabbatOnOrAfter,
} from '@shared/hebrewCalendar.ts';

export { toDateKey, fromDateKey };

const gregFmt = new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'numeric', year: 'numeric' });
const gregLongFmt = new Intl.DateTimeFormat('he-IL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit' });
const weekdayFmt = new Intl.DateTimeFormat('he-IL', { weekday: 'long' });

export interface DateLabels {
  dateKey: string;
  hebrew: string;          // "כ״ט תשרי תשפ״ז"
  gregorian: string;       // "10.10.2026"
  gregorianLong: string;   // "יום שבת, 10 באוקטובר 2026"
  weekday: string;
  parasha: string | null;  // Shabbat only
  holidays: string[];
}

export function asDate(x: Date | string): Date {
  return typeof x === 'string' ? fromDateKey(x) : x;
}

export function dateLabels(x: Date | string): DateLabels {
  const d = asDate(x);
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return {
    dateKey: toDateKey(day),
    hebrew: hebrewDateString(day),
    gregorian: gregFmt.format(day).replace(/\//g, '.'),
    gregorianLong: gregLongFmt.format(day),
    weekday: weekdayFmt.format(day),
    parasha: isShabbat(day) ? parashaLabel(day) : null,
    holidays: holidaysOn(day),
  };
}

/** Timestamp-ish (Firestore Timestamp | Date | ISO string) → { date labels, time }. */
export function instantLabels(v: unknown): (DateLabels & { time: string }) | null {
  let d: Date | null = null;
  if (v instanceof Date) d = v;
  else if (v && typeof (v as { toDate?: () => Date }).toDate === 'function') d = (v as { toDate: () => Date }).toDate();
  else if (typeof v === 'string' && v) d = new Date(v);
  if (!d || Number.isNaN(d.getTime())) return null;
  return { ...dateLabels(d), time: timeFmt.format(d) };
}

export function upcomingShabbat(from = new Date()): DateLabels {
  return dateLabels(shabbatOnOrAfter(from));
}

export function todayKey(): string {
  return toDateKey(new Date());
}
